import { Box, Input, Text } from "@chakra-ui/react";
import { Building2, Users } from "lucide-react";
import {
  EyebrowBadge,
  FormField,
  OnboardingComboBox,
  SectionCard,
  SelectableCard,
} from "../components";
import type { CatalogItem, FormData } from "../types";

export function Step2Profile({
  data,
  cities,
  updateData,
  fieldErrors,
}: {
  data: FormData;
  cities: CatalogItem[];
  updateData: (field: string, value: string | boolean) => void;
  fieldErrors: Record<string, string>;
}) {
  return (
    <Box spaceY={8}>
      <Box spaceY={3}>
        <EyebrowBadge stepId={2} />
        <Box spaceY={2}>
          <Text
            fontSize="3xl"
            fontWeight="bold"
            color="gray.900"
            letterSpacing="tight"
          >
            Perfil del comercio
          </Text>
          <Text fontSize="lg" color="gray.500" maxW="2xl">
            Empecemos con la información principal de tu operación. Esta base
            nos permite preparar el alta del recaudo con menos idas y vueltas.
          </Text>
        </Box>
      </Box>

      <SectionCard
        title="Tipo de persona"
        description="Define la estructura legal con la que opera el comercio."
      >
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
          gap={4}
        >
          <SelectableCard
            selected={data.tipoPersona}
            onSelect={(val) => updateData("tipoPersona", val)}
            id="natural"
            title="Persona natural"
            description="Operas bajo tu propio nombre y RUT personal."
            icon={Users}
          />
          <SelectableCard
            selected={data.tipoPersona}
            onSelect={(val) => updateData("tipoPersona", val)}
            id="juridica"
            title="Persona jurídica"
            description="Empresa constituida con NIT y representación legal."
            icon={Building2}
          />
        </Box>
      </SectionCard>

      <SectionCard
        title="Datos del comercio"
        description="Usaremos esta información como referencia principal para compliance y configuración operativa."
      >
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
          gap={6}
        >
          <FormField
            label={
              data.tipoPersona === "juridica"
                ? "Razón social"
                : "Nombre completo"
            }
            error={fieldErrors.nombre}
          >
            <Input
              value={data.nombre}
              onChange={(e) => updateData("nombre", e.target.value)}
              placeholder="Como aparece en el RUT"
            />
          </FormField>
          <FormField
            label={data.tipoPersona === "juridica" ? "NIT" : "Cédula"}
            error={fieldErrors.identificacion}
          >
            <Input
              value={data.identificacion}
              onChange={(e) => updateData("identificacion", e.target.value)}
              placeholder="Ej. 9001234567"
            />
          </FormField>
          <OnboardingComboBox
            label="Ciudad de registro (RUT)"
            items={cities}
            value={data.ciudad}
            onValueChange={(value) => updateData("ciudad", value)}
            error={fieldErrors.ciudad}
            placeholder="Busca una ciudad..."
          />
          {data.ciudad === "other" && (
            <FormField
              label="¿Cuál es tu ciudad?"
              error={fieldErrors.otraCiudad}
            >
              <Input
                value={data.otraCiudad}
                onChange={(e) => updateData("otraCiudad", e.target.value)}
                placeholder="Escribe el nombre de tu ciudad"
              />
            </FormField>
          )}
          <FormField label="Dirección principal" error={fieldErrors.direccion}>
            <Input
              value={data.direccion}
              onChange={(e) => updateData("direccion", e.target.value)}
              placeholder="Calle 123 # 45-67"
            />
          </FormField>
          <FormField label="Código CIIU" error={fieldErrors.ciiu}>
            <Input
              value={data.ciiu}
              onChange={(e) => updateData("ciiu", e.target.value)}
              placeholder="Ej. 6810"
            />
          </FormField>
          <FormField
            label="Página web"
            tooltip="También sirve el enlace de una red social (Instagram, Facebook, LinkedIn, etc.). Debe incluir https://"
            error={fieldErrors.web}
          >
            <Input
              value={data.web}
              onChange={(e) => updateData("web", e.target.value)}
              placeholder="https://www.tuempresa.com"
            />
          </FormField>
        </Box>
      </SectionCard>

      {data.tipoPersona === "juridica" && (
        <SectionCard
          title="Representante legal"
          description="La persona autorizada para representar a la empresa."
        >
          <Box
            display="grid"
            gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap={6}
          >
            <FormField
              label="Nombre completo"
              error={fieldErrors.representanteLegalNombre}
            >
              <Input
                value={data.representanteLegalNombre}
                onChange={(e) =>
                  updateData("representanteLegalNombre", e.target.value)
                }
                placeholder="Ej. María Pérez"
              />
            </FormField>
            <FormField
              label="Cédula de ciudadanía"
              error={fieldErrors.representanteLegalIdentificacion}
            >
              <Input
                value={data.representanteLegalIdentificacion}
                onChange={(e) =>
                  updateData("representanteLegalIdentificacion", e.target.value)
                }
                placeholder="Ej. 1234567890"
              />
            </FormField>
          </Box>
        </SectionCard>
      )}
    </Box>
  );
}
